import {
  createWebHashHistory,
  createRouter,
  type Router,
  type RouteRecordNameGeneric,
} from 'vue-router'
import { handlePlatformOAuthRedirect } from '@/utils/platformOAuthRedirect'
import { getAccessToken } from '@/services/interceptors'
import { useApplication } from '@/store/modules/App/application'

import routes from './routes'
import { storeToRefs } from 'pinia'

const disableScroll = ['discussionId']
const routesToForceReload = new Set([
  'ListSpacesRoute',
  'ListStoriesRoute',
  'DashboardRoute',
])

function has24HoursPassed(storedDate: string | Date): boolean {
  const saved = new Date(storedDate)
  const now = new Date()
  const diffInMs = now.getTime() - saved.getTime()
  const hoursPassed = diffInMs / (1000 * 60 * 60)
  return hoursPassed >= 24
}

const router = createRouter({
  history: createWebHashHistory(process.env.BASE_URL),
  linkActiveClass: 'active',
  linkExactActiveClass: 'exact-active',
  scrollBehavior(to, from) {
    const foundKeyOnQuery = disableScroll.some(
      ($d) => $d in to.query || from.query,
    )
    if (foundKeyOnQuery) {
      return false
    }

    return { left: 0, top: 0 }
  },
  routes,
})

router.beforeEach((to, from, next) => {
  ;(
    router as Router & { prevRouteName: RouteRecordNameGeneric }
  ).prevRouteName = from.name
  const isLogin = to.name === 'Login'
  const isNotFound = to.name === 'NotFound'
  const keepRoute = to?.meta?.keepRoute === true
  const isOutsideApp = to?.meta?.isOutsideApp === true

  const token = getAccessToken()

  // Visit ./src/utils/platformOAuthRedirect.ts for more details.
  handlePlatformOAuthRedirect(to, next)

  if (isNotFound) {
    if (to.path && to.path.indexOf('/!/') === 0) {
      next({ path: to.path.replace('/!', ''), query: to.query })
    }

    next()
  } else if (isOutsideApp && token) {
    if (keepRoute) {
      // go to provided route if specified
      next()
    } else {
      // if the user is logged in and trying to access outside app routes, redirect to the dashboard
      next({ name: 'me' })
    }
  } else if (isOutsideApp && !token) {
    // if the user is not logged in and the route is outside the app,
    next()
  } else if (!isLogin && !token) {
    // if the user is not logged in, redirect to login
    next({
      name: 'Login',
      query: {
        redirect: encodeURIComponent(to.fullPath),
      },
    })
  } else {
    next()
  }
})

router.afterEach((to) => {
  const appOpenedAt = storeToRefs(useApplication()).openedAt
  const forceReload =
    typeof to.name === 'string' &&
    routesToForceReload.has(to.name) &&
    Boolean(appOpenedAt.value) &&
    has24HoursPassed(appOpenedAt.value)

  if (forceReload) {
    window.location.reload()
  }
})

export default router
